import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { AIAgentsService, AIAgentWithStats } from "@/lib/services/ai-agents";

export function useAIAgents() {
  const { user } = useAuth();
  const [agents, setAgents] = useState<AIAgentWithStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all agents with stats
  const loadAgents = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const agentsData = await AIAgentsService.getUserAgentsWithStats(user.id);
      setAgents(agentsData);
    } catch (err) {
      console.error("Error loading agents:", err);
      setError("Error al cargar los agentes de IA");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Track usage of an agent
  const trackUsage = useCallback(
    async (agentId: string, metadata: Record<string, unknown> = {}) => {
      if (!user) return;

      try {
        await AIAgentsService.trackAgentUsage(user.id, agentId, metadata);
        // Refresh agents data after tracking usage
        await loadAgents();
      } catch (err) {
        console.error("Error tracking usage:", err);
      }
    },
    [user, loadAgents]
  );

  // Get most used agents
  const getMostUsedAgents = useCallback(
    async (limit: number = 5) => {
      if (!user) return [];

      try {
        return await AIAgentsService.getMostUsedAgents(user.id, limit);
      } catch (err) {
        console.error("Error getting most used agents:", err);
        return [];
      }
    },
    [user]
  );

  // Get recently used agents
  const getRecentlyUsedAgents = useCallback(
    async (limit: number = 5) => {
      if (!user) return [];

      try {
        return await AIAgentsService.getRecentlyUsedAgents(user.id, limit);
      } catch (err) {
        console.error("Error getting recently used agents:", err);
        return [];
      }
    },
    [user]
  );

  // Get agent by ID
  const getAgentById = useCallback(
    (agentId: string) => {
      return agents.find((agent) => agent.id === agentId);
    },
    [agents]
  );

  // Get active agents only
  const getActiveAgents = useCallback(() => {
    return agents.filter((agent) => agent.maintenance_status === "active");
  }, [agents]);

  // Get agents under maintenance
  const getMaintenanceAgents = useCallback(() => {
    return agents.filter((agent) => agent.maintenance_status === "maintenance");
  }, [agents]);

  // Get disabled agents
  const getDisabledAgents = useCallback(() => {
    return agents.filter((agent) => agent.maintenance_status === "disabled");
  }, [agents]);

  // Load agents on mount and when user changes
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  return {
    agents,
    loading,
    error,
    trackUsage,
    getMostUsedAgents,
    getRecentlyUsedAgents,
    getAgentById,
    getActiveAgents,
    getMaintenanceAgents,
    getDisabledAgents,
    refresh: loadAgents,
  };
}
